import ButtonV2, { getButtonClasses } from "components/elements/buttonV2"
import Image from "components/elements/image"
import YoutubeVideo from "components/elements/youtubeVideo"
import TextLockupV2 from "components/elements/textLockupV2"
import PropTypes from "prop-types"
import React from "react"
import { usePodcastEpisodes } from "services/graphQl/queries/podcastEpisodes"
import appleIcon from "./apple.svg"
import "./section-darkmode-podcast-highlight-v2.scss"
import spotifyIcon from "./spotify.svg"
import youtubeIcon from "./youtube.png"

// One episode: its YouTube thumbnail (plays on the page), title, details
// and listen links. The newest episode is the large featured card.
const EpisodeCard = ({
  applePodcastLink,
  coverImage,
  durationMinutes,
  featured,
  releaseDate,
  slug,
  spotifyLink,
  summary,
  title,
  youtubeLink,
}) => {
  // The episode's own page (see gatsby/podcastEpisodePages.js).
  const titleLink = slug ? <a href={`/podcast/${slug}`}>{title}</a> : title
  const details = [
    releaseDate,
    durationMinutes ? `${durationMinutes} min` : null,
  ].filter(Boolean)
  const links = [
    ["Apple Podcasts", applePodcastLink, appleIcon],
    ["Spotify", spotifyLink, spotifyIcon],
    ["YouTube", youtubeLink, youtubeIcon],
  ].filter(([, href]) => href)

  return (
    <article
      className={`section-darkmode-podcast-highlight-v2--episode${
        featured
          ? " section-darkmode-podcast-highlight-v2--episode-featured"
          : ""
      }`}
    >
      <div className="section-darkmode-podcast-highlight-v2--episode-video">
        {youtubeLink ? (
          <YoutubeVideo title={title} url={youtubeLink} />
        ) : (
          // No YouTube video found for this episode: show the podcast cover
          // and link to the episode on Apple Podcasts or Spotify instead.
          <a
            aria-label={`Listen to ${title} (opens in a new tab)`}
            className="section-darkmode-podcast-highlight-v2--episode-cover"
            href={applePodcastLink || spotifyLink}
            rel="noopener noreferrer"
            target="_blank"
          >
            {coverImage?.fluid && <Image image={coverImage.fluid} />}
          </a>
        )}
      </div>
      <div className="section-darkmode-podcast-highlight-v2--episode-body">
        {featured ? (
          <h3 className="section-darkmode-podcast-highlight-v2--episode-title">
            {titleLink}
          </h3>
        ) : (
          <h4 className="section-darkmode-podcast-highlight-v2--episode-title">
            {titleLink}
          </h4>
        )}
        {details.length > 0 && (
          <p className="section-darkmode-podcast-highlight-v2--episode-details">
            {details.join(" | ")}
          </p>
        )}
        {featured && summary?.summary && (
          <p className="section-darkmode-podcast-highlight-v2--episode-summary">
            {summary.summary}
          </p>
        )}
        {links.length > 0 && (
          <p className="section-darkmode-podcast-highlight-v2--episode-links">
            {links.map(([name, href, icon]) => (
              <a
                aria-label={`Listen to ${title} on ${name} (opens in a new tab)`}
                href={href}
                key={name}
                rel="noopener noreferrer"
                target="_blank"
              >
                <img alt="" height="20" src={icon} width="20" />
              </a>
            ))}
          </p>
        )}
      </div>
    </article>
  )
}

EpisodeCard.propTypes = {
  applePodcastLink: PropTypes.string,
  coverImage: PropTypes.object,
  durationMinutes: PropTypes.number,
  featured: PropTypes.bool,
  releaseDate: PropTypes.string,
  slug: PropTypes.string,
  spotifyLink: PropTypes.string,
  summary: PropTypes.shape({ summary: PropTypes.string }),
  title: PropTypes.string.isRequired,
  youtubeLink: PropTypes.string,
}

const SectionDarkmodePodcastHighlightV2 = ({
  allEpisodesLink,
  applePodcastLink,
  image,
  showRecentEpisodes,
  spotifyLink,
  text,
  title,
  youtubeLink,
}) => {
  // The podcast's five newest episodes, when "Show recent episodes" is on.
  const feed = usePodcastEpisodes()
  const recent = showRecentEpisodes ? feed : []

  return (
    <section
      className={`section-darkmode-podcast-highlight-v2${
        recent.length > 0
          ? " section-darkmode-podcast-highlight-v2--with-episodes"
          : ""
      }`}
    >
      <div className="container-v2">
        {image?.fluid && (
          <div className="section-darkmode-podcast-highlight-v2--image">
            <Image image={image?.fluid} />
          </div>
        )}
        <div className="section-darkmode-podcast-highlight-v2--content">
          <div className="section-darkmode-podcast-highlight-v2--content-header">
            {title && (
              <h2 className="section-darkmode-podcast-highlight-v2--title">
                <TextLockupV2>{title}</TextLockupV2>
              </h2>
            )}
            {text?.childrenMarkdownRemark?.[0]?.html && (
              <div
                className="section-darkmode-podcast-highlight-v2--text"
                dangerouslySetInnerHTML={{
                  __html: text?.childrenMarkdownRemark?.[0]?.html,
                }}
              />
            )}
          </div>

          <div className="section-darkmode-podcast-highlight-v2--sub-title-links-wrapper">
            <h3 className="section-darkmode-podcast-highlight-v2--sub-title">
              Listen on:
            </h3>
            <div className="section-darkmode-podcast-highlight-v2--links">
              {spotifyLink && (
                <p className="section-darkmode-podcast-highlight-v2--cta-button-wrapper section-darkmode-podcast-highlight-v2--spotify-button">
                  <ButtonV2
                    accessibilityLabel="Spotify"
                    className={getButtonClasses("Outline White", "small")}
                    href={spotifyLink}
                    key={spotifyLink}
                    newWindow={true}
                    showArrow={false}
                    text="Spotify"
                  />
                </p>
              )}
              {applePodcastLink && (
                <p className="section-darkmode-podcast-highlight-v2--cta-button-wrapper section-darkmode-podcast-highlight-v2--apple-podcasts-button">
                  <ButtonV2
                    accessibilityLabel="Apple Podcasts"
                    className={getButtonClasses("Outline White", "small")}
                    href={applePodcastLink}
                    key={applePodcastLink}
                    newWindow={true}
                    showArrow={false}
                    text="Apple Podcasts"
                  />
                </p>
              )}
              {youtubeLink && (
                <p className="section-darkmode-podcast-highlight-v2--cta-button-wrapper section-darkmode-podcast-highlight-v2--youtube-button">
                  <ButtonV2
                    accessibilityLabel="YouTube"
                    className={getButtonClasses("Outline White", "small")}
                    href={youtubeLink}
                    key={youtubeLink}
                    newWindow={true}
                    showArrow={false}
                    text="YouTube"
                  />
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {recent.length > 0 && (
        <div className="container-v2 section-darkmode-podcast-highlight-v2--episodes">
          <h3 className="section-darkmode-podcast-highlight-v2--episodes-title">
            <span className="section-darkmode-podcast-highlight-v2--episodes-title-phone">
              Latest episode
            </span>
            <span className="section-darkmode-podcast-highlight-v2--episodes-title-wide">
              Recent episodes
            </span>
          </h3>
          <div className="section-darkmode-podcast-highlight-v2--episodes-grid">
            <EpisodeCard {...recent[0]} coverImage={image} featured />
            {recent.length > 1 && (
              <div className="section-darkmode-podcast-highlight-v2--episodes-more">
                {recent.slice(1).map(episode => (
                  <EpisodeCard
                    coverImage={image}
                    key={episode.id}
                    {...episode}
                  />
                ))}
              </div>
            )}
          </div>
          {allEpisodesLink && (
            <p className="section-darkmode-podcast-highlight-v2--all-episodes">
              <ButtonV2
                className={getButtonClasses("Outline White", "small")}
                href={allEpisodesLink}
                showArrow={true}
                text="Explore all episodes"
              />
            </p>
          )}
        </div>
      )}
    </section>
  )
}

SectionDarkmodePodcastHighlightV2.propTypes = {
  allEpisodesLink: PropTypes.string,
  applePodcastLink: PropTypes.string.isRequired,
  image: PropTypes.object.isRequired,
  showRecentEpisodes: PropTypes.bool,
  spotifyLink: PropTypes.string.isRequired,
  text: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  youtubeLink: PropTypes.string.isRequired,
}
export default SectionDarkmodePodcastHighlightV2
